import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Archive,
  CheckCircle2,
  CircleDashed,
  CircleDot,
  Clock3,
  Inbox,
  Layers,
  PauseCircle,
  Plus,
  Search,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useTeams } from '@/api/teams';
import { useTicketCounts, useTicketsInfinite, type TicketFilters } from '@/api/tickets';
import { useAllUsers } from '@/api/users';
import type { Ticket, TicketCounts } from '@/api/types';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ListSkeleton,
  PageHeader,
  Select,
} from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { cn } from '@/lib/cn';
import { formatDateTime, formatRelative } from '@/lib/format';
import { ACTIVE, CHANNELS, CHANNEL_LABEL, PRIORITIES, PRIORITY_LABEL } from '../tickets/meta';
import { NewTicketModal } from '../tickets/NewTicketModal';
import { ChannelIcon, PriorityBadge, SlaBadge, StatusBadge } from '../tickets/TicketBadges';
import { useDebounced } from '../tickets/useDebounced';

type ViewId = 'mine' | 'unassigned' | 'open' | 'pending' | 'on_hold' | 'solved' | 'closed' | 'all';

interface View {
  id: ViewId;
  label: string;
  icon: LucideIcon;
  filter: TicketFilters;
  count: keyof TicketCounts;
}

const VIEW_GROUPS: { heading: string; views: View[] }[] = [
  {
    heading: 'Queues',
    views: [
      {
        id: 'mine',
        label: 'Assigned to me',
        icon: UserRound,
        filter: { assignee: 'me', status: ACTIVE },
        count: 'mine',
      },
      {
        id: 'unassigned',
        label: 'Unassigned',
        icon: CircleDashed,
        filter: { assignee: 'unassigned', status: ACTIVE },
        count: 'unassigned',
      },
    ],
  },
  {
    heading: 'Status',
    views: [
      { id: 'open', label: 'Open', icon: CircleDot, filter: { status: 'open' }, count: 'open' },
      { id: 'pending', label: 'Pending', icon: Clock3, filter: { status: 'pending' }, count: 'pending' },
      { id: 'on_hold', label: 'On hold', icon: PauseCircle, filter: { status: 'on_hold' }, count: 'on_hold' },
      { id: 'solved', label: 'Solved', icon: CheckCircle2, filter: { status: 'solved' }, count: 'solved' },
      { id: 'closed', label: 'Closed', icon: Archive, filter: { status: 'closed' }, count: 'closed' },
      { id: 'all', label: 'All tickets', icon: Layers, filter: {}, count: 'all' },
    ],
  },
];
const VIEWS = VIEW_GROUPS.flatMap((g) => g.views);

export function InboxPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const view = VIEWS.find((v) => v.id === params.get('view')) ?? VIEWS[0]!;
  const urlQ = params.get('q') ?? '';
  const [q, setQ] = useState(urlQ);
  const [priority, setPriority] = useState('');
  const [channel, setChannel] = useState('');
  const [teamId, setTeamId] = useState('');
  const [creating, setCreating] = useState(false);
  const debouncedQ = useDebounced(q.trim(), 300);

  // The header's quick search navigates here with ?q=…
  useEffect(() => setQ(urlQ), [urlQ]);

  const counts = useTicketCounts();
  const teams = useTeams();
  const filters: TicketFilters = {
    ...view.filter,
    q: debouncedQ || undefined,
    priority: (priority || undefined) as TicketFilters['priority'],
    channel: (channel || undefined) as TicketFilters['channel'],
    teamId: teamId || undefined,
  };
  const tickets = useTicketsInfinite(filters);
  const rows = tickets.data?.pages.flatMap((p) => p.data) ?? [];
  const filtered = !!(debouncedQ || priority || channel || teamId);

  const setView = (id: ViewId) => setParams(id === 'mine' ? {} : { view: id }, { replace: true });

  return (
    <>
      <PageHeader
        eyebrow="Support"
        title="Inbox"
        description="Tickets from email, chat, WhatsApp and your app in one place."
        actions={
          can('create', 'Ticket') && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New ticket
            </Button>
          )
        }
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <nav
          aria-label="Inbox views"
          className="-mx-4 flex gap-1 overflow-x-auto px-4 lg:sticky lg:top-24 lg:mx-0 lg:w-56 lg:shrink-0 lg:flex-col lg:gap-5 lg:self-start lg:overflow-visible lg:px-0"
        >
          {VIEW_GROUPS.map((group) => (
            <div key={group.heading} className="flex gap-1 lg:flex-col">
              <p className="mb-1 hidden px-3 text-[10px] font-semibold tracking-[0.14em] text-slate-400 uppercase lg:block">
                {group.heading}
              </p>
              {group.views.map((v) => {
                const n = counts.data?.[v.count];
                const active = v.id === view.id;
                const Icon = v.icon;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setView(v.id)}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium whitespace-nowrap transition-all',
                      active
                        ? 'text-brand-700 shadow-card bg-white ring-1 ring-slate-200/80'
                        : 'text-slate-600 hover:bg-white/80 hover:text-slate-900',
                    )}
                  >
                    <Icon className={cn('h-4 w-4', active ? 'text-brand-500' : 'text-slate-400')} />
                    {v.label}
                    {n !== undefined && (
                      <span
                        className={cn(
                          'ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                          active ? 'bg-brand-50 text-brand-700' : 'text-slate-400',
                        )}
                      >
                        {n}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <Card className="min-w-0 flex-1 overflow-hidden">
          <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 p-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                aria-label="Search tickets"
                placeholder={`Search in ${view.label.toLowerCase()}…`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="focus:border-brand-400 focus:ring-brand-500/15 h-10 w-full rounded-xl border border-slate-200/90 bg-white pr-3 pl-9 text-sm shadow-xs placeholder:text-slate-400 focus:ring-4 focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-3 gap-2 sm:flex">
              <Select
                aria-label="Priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                wrapperClassName="sm:w-32"
              >
                <option value="">Priority</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABEL[p]}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                wrapperClassName="sm:w-36"
              >
                <option value="">Channel</option>
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {CHANNEL_LABEL[c]}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Team"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                wrapperClassName="sm:w-44"
              >
                <option value="">All teams</option>
                {(teams.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 text-xs text-slate-500">
            <span className="font-medium text-slate-700">{view.label}</span>
            <span className="flex items-center gap-3">
              {filtered && (
                <button
                  type="button"
                  className="text-brand-600 font-medium hover:underline"
                  onClick={() => {
                    setQ('');
                    setPriority('');
                    setChannel('');
                    setTeamId('');
                  }}
                >
                  Clear filters
                </button>
              )}
              {tickets.isFetching && !tickets.isPending && <span className="text-slate-400">Updating…</span>}
            </span>
          </div>

          {tickets.isPending ? (
            <ListSkeleton label="Loading tickets" />
          ) : tickets.error ? (
            <ErrorState error={tickets.error} onRetry={() => void tickets.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Inbox className="h-6 w-6" />}
              title={
                filtered
                  ? 'No tickets match these filters'
                  : view.id === 'mine'
                    ? 'You’re all caught up'
                    : `Nothing in “${view.label}”`
              }
              description={
                view.id === 'mine' && !filtered ? 'Take a ticket from Unassigned to get started.' : undefined
              }
            />
          ) : (
            <>
              <ul className="divide-y divide-slate-100">
                {rows.map((t) => (
                  <TicketRow key={t.id} ticket={t} />
                ))}
              </ul>
              {tickets.hasNextPage && (
                <div className="flex justify-center border-t border-slate-100 p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={tickets.isFetchingNextPage}
                    onClick={() => void tickets.fetchNextPage()}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {creating && <NewTicketModal onClose={() => setCreating(false)} />}
    </>
  );
}

function TicketRow({ ticket: t }: { ticket: Ticket }) {
  const users = useAllUsers();
  const assignee = t.assigneeId ? users.data?.find((u) => u.id === t.assigneeId) : undefined;
  const awaitingUs =
    t.lastMessageAuthorType === 'customer' && (t.status === 'open' || t.status === 'on_hold');
  const name = t.customer?.name ?? 'Unknown customer';

  return (
    <li>
      <Link
        to={`/inbox/${t.id}`}
        className={cn(
          'group relative flex gap-3.5 px-4 py-4 transition-colors hover:bg-slate-50/80 sm:px-5',
          t.priority === 'urgent' && t.status === 'open' && 'bg-red-50/30',
        )}
      >
        {awaitingUs && (
          <span className="bg-brand-500 absolute inset-y-3 left-0 w-0.5 rounded-r-full" aria-hidden />
        )}
        <Avatar name={name} src={t.customer?.avatarUrl} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'truncate text-sm',
                awaitingUs ? 'font-semibold text-slate-900' : 'font-medium text-slate-700',
              )}
            >
              {name}
            </span>
            <ChannelIcon channel={t.channel} />
            <span className="font-mono text-[11px] text-slate-400">#{t.number}</span>
            <time
              dateTime={t.lastMessageAt}
              title={formatDateTime(t.lastMessageAt)}
              className={cn(
                'ml-auto shrink-0 text-xs',
                awaitingUs ? 'text-brand-600 font-medium' : 'text-slate-400',
              )}
            >
              {formatRelative(t.lastMessageAt)}
            </time>
          </div>
          <p
            className={cn(
              'mt-0.5 truncate text-[15px]',
              awaitingUs ? 'font-semibold text-slate-900' : 'text-slate-800',
            )}
          >
            {t.subject}
          </p>
          <p className="mt-0.5 truncate text-sm text-slate-500">
            {t.lastMessageAuthorType === 'agent' && <span className="text-slate-400">↩ </span>}
            {t.lastMessagePreview}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <StatusBadge status={t.status} />
            <PriorityBadge priority={t.priority} />
            <SlaBadge ticket={t} />
            {t.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500"
              >
                {tag}
              </span>
            ))}
            <span className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">
              {assignee ? (
                <>
                  <Avatar name={assignee.name} src={assignee.avatarUrl} size="xs" />
                  <span className="hidden sm:inline">{assignee.name}</span>
                </>
              ) : (
                <span className="rounded-md border border-dashed border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-400">
                  Unassigned
                </span>
              )}
            </span>
          </div>
        </div>
      </Link>
    </li>
  );
}
